import { createContext, useState, useContext, useCallback } from "react";
import {
  getSubscriptionAPICall,
  subscribeToOfferAPICall,
  cancelSubscriptionAPICall,
  getDashboardURLAPICall,
  getNumberOfConfirmedUsersAPICall,
  getOffersAPICall,
  getUsageAPICall,
  getUsageCardsAPICall,
  getEmbedKeyUsageAPICall,
  getEmbedDomainUsageAPICall,
  canAddApiProjectUsersAPICall,
  getSeatsAPICall,
  getApiKeysAPICall,
  getPlatformSubscriptionAPICall,
  getPlatformProductsAPICall,
  getProductUsageAPICall,
  getProductUsageCardsAPICall,
  getProductUsageTrendAPICall,
  getProductUsageBreakdownAPICall,
  subscribeToPlanAPICall,
  addProductAPICall,
  removeProductAPICall,
  changeProductPlanAPICall,
  cancelPlatformSubscriptionAPICall,
  getPlatformDashboardURLAPICall,
} from "../api/licensing/licensingService";

export const LicensingContext = createContext({
  subscribeToOffer: () => {},
  subscription: null,
  cancelSubscription: () => {},
  subscriptionDetails: () => {},
  dashboardURL: () => {},
  setSubscription: () => {},
  numberOfConfirmedUsers: () => {},
  getUsage: () => {},
  getUsageCards: () => {},
  getEmbedKeyUsage: () => {},
  getEmbedDomainUsage: () => {},
  getSeats: () => {},
  getApiKeys: () => {},
  canAddUsers: () => {},
  offers: () => {},
  getPlatformSubscription: () => {},
  platformProducts: [],
  getPlatformProducts: () => {},
  getProductUsage: () => {},
  getProductUsageCards: () => {},
  getProductUsageTrend: () => {},
  getProductUsageBreakdown: () => {},
  subscribeToPlan: () => {},
  addProduct: () => {},
  removeProduct: () => {},
  changeProductPlan: () => {},
  cancelPlatformSubscription: () => {},
  getPlatformDashboardURL: () => {},
  purchaseSubscription: () => Promise.resolve(),
});

const LicensingContextProvider = ({ children, setLoading }) => {
  const [subscription, setSubscription] = useState(undefined);
  const [platformProducts, setPlatformProducts] = useState([]);

  const subscribeToOffer = useCallback(
    async (projectId, offerName, currency, onSuccess, onError) => {
      subscribeToOfferAPICall(projectId, offerName, currency, onSuccess, onError);
    }
  );

  const cancelSubscription = useCallback(
    async (projectId, onSuccess, onError) => {
      cancelSubscriptionAPICall(projectId, onSuccess, onError);
    }
  );

  const subscriptionDetails = useCallback(
    async (projectId, onSuccess, onError) => {
      getSubscriptionAPICall(projectId, onSuccess, onError);
    }
  );

  const numberOfConfirmedUsers = useCallback(
    async (projectId, onSuccess, onError, productSlug) => {
      getNumberOfConfirmedUsersAPICall(projectId, onSuccess, onError, productSlug);
    }
  );

  const dashboardURL = useCallback(async (projectId, onSuccess, onError) => {
    getDashboardURLAPICall(projectId, onSuccess, onError);
  });

  const offers = useCallback(async (onSuccess, onError) => {
    getOffersAPICall(onSuccess, onError);
  });

  const getUsage = useCallback(async (projectId, data, onSuccess, onError) => {
    getUsageAPICall(projectId, data, onSuccess, onError);
  });

  const getSeats = useCallback(
    async (projectId, data, onSuccess, onError) => {
      getSeatsAPICall(projectId, data, onSuccess, onError);
    },
    [] 
  );

  const getApiKeys = useCallback(
    async (projectId, data, onSuccess, onError) => {
      getApiKeysAPICall(projectId, data, onSuccess, onError);
    },
    [] 
  );

  const getUsageCards = useCallback(
    async (projectId, data, onSuccess, onError) => {
      getUsageCardsAPICall(projectId, data, onSuccess, onError);
    }
  );

  const getEmbedKeyUsage = useCallback(
    async (projectId, params, onSuccess, onError) => {
      getEmbedKeyUsageAPICall(projectId, params, onSuccess, onError);
    }
  );

  const getEmbedDomainUsage = useCallback(
    async (projectId, params, onSuccess, onError) => {
      getEmbedDomainUsageAPICall(projectId, params, onSuccess, onError);
    }
  );

  const canAddUsers = useCallback(async (emails, projectId, onSuccess, onError) => {
    canAddApiProjectUsersAPICall(emails, projectId, onSuccess, onError);
  }, []);


  const getPlatformSubscription = useCallback(
    async (projectId, onSuccess, onError) => {
      getPlatformSubscriptionAPICall(projectId, (response) => {
        setSubscription(response.data.details || null);
        onSuccess(response);
      }, onError);
    },
    []
  );

  const getPlatformProducts = useCallback(
    async (projectId, onSuccess, onError) => {
        getPlatformProductsAPICall(projectId, (response) => {
            const products = response.data.products
                ?.filter(p => p.active)
                ?.map(p => ({
                    name: p.name,
                    slug: p.slug,
                    has_subscriptions: p.has_subscriptions
                })) ?? [];
            setPlatformProducts(products);
            onSuccess(response);
        }, onError);
    },
    []
  );

  const getProductUsage = useCallback(
    async (projectId, productSlug, params, onSuccess, onError) => {
      getProductUsageAPICall(projectId, productSlug, params, onSuccess, onError);
    },
    []
  );

  const getProductUsageCards = useCallback(
    async (projectId, productSlug, params, onSuccess, onError) => {
      getProductUsageCardsAPICall(projectId, productSlug, params, onSuccess, onError);
    },
    []
  );

  const getProductUsageTrend = useCallback(
    async (projectId, productSlug, params, onSuccess, onError) => {
      getProductUsageTrendAPICall(projectId, productSlug, params, onSuccess, onError);
    },
    []
  );

  const getProductUsageBreakdown = useCallback(
    async (projectId, productSlug, params, onSuccess, onError) => {
      getProductUsageBreakdownAPICall(projectId, productSlug, params, onSuccess, onError);
    },
    []
  );

  const subscribeToPlan = useCallback(
    async (projectId, planId, currency, onSuccess, onError) => {
      subscribeToPlanAPICall(projectId, planId, currency, onSuccess, onError);
    },
    []
  );

  const addProduct = useCallback(
    async (projectId, planId, currency, onSuccess, onError) => {
      addProductAPICall(projectId, planId, currency, onSuccess, onError);
    },
    []
  );

  const removeProduct = useCallback(
    async (projectId, productSlug, onSuccess, onError) => {
      removeProductAPICall(projectId, productSlug, onSuccess, onError);
    },
    []
  );

  const changeProductPlan = useCallback(
    async (projectId, productSlug, planId, currency, onSuccess, onError) => {
      changeProductPlanAPICall(projectId, productSlug, planId, currency, onSuccess, onError);
    },
    []
  );

  const cancelPlatformSubscription = useCallback(
    async (projectId, onSuccess, onError) => {
      cancelPlatformSubscriptionAPICall(projectId, onSuccess, onError);
    },
    []
  );

  const getPlatformDashboardURL = useCallback(
    async (projectId, onSuccess, onError) => {
      getPlatformDashboardURLAPICall(projectId, onSuccess, onError);
    },
    []
  );

  const purchaseSubscription = useCallback(
    async (projectId, planId, currency) => {
      return new Promise((resolve, reject) => {
        const handleSuccess = (response) => {
          if (response.data.url) {
            resolve({ redirect: true, url: response.data.url });
          } else {
            getPlatformSubscription(
              projectId,
              () => resolve({ redirect: false }),
              () => reject(new Error("Failed to refresh subscription"))
            );
          }
        };

        if (!subscription) {
          subscribeToPlanAPICall(projectId, planId, currency, handleSuccess, reject);
        } else {
          addProductAPICall(projectId, planId, currency, handleSuccess, reject);
        }
      });
    },
    [subscription, getPlatformSubscription]
  );

  return (
    <LicensingContext.Provider
      value={{
        subscribeToOffer,
        cancelSubscription,
        subscriptionDetails,
        dashboardURL,
        subscription,
        setSubscription,
        numberOfConfirmedUsers,
        offers,
        getUsage,
        getUsageCards,
        getEmbedKeyUsage,
        getEmbedDomainUsage,
        canAddUsers,
        getSeats,
        getApiKeys,
        getPlatformSubscription,
        platformProducts,
        getPlatformProducts,
        getProductUsage,
        getProductUsageCards,
        getProductUsageTrend,
        getProductUsageBreakdown,
        subscribeToPlan,
        addProduct,
        removeProduct,
        changeProductPlan,
        cancelPlatformSubscription,
        getPlatformDashboardURL,
        purchaseSubscription,
      }}
    >
      {children}
    </LicensingContext.Provider>
  );
};

export const useLicensingContext = () => useContext(LicensingContext);

export default LicensingContextProvider;
